/**
 * FilterContext - Sistema global de filtros para o Dashboard
 *
 * Similar ao DateRangeContext, fornece filtros centralizados para todas as páginas.
 * Sincroniza entre abas e persiste no localStorage.
 *
 * Para usuários com restrição de loja (loja_restrita), o filtro é automaticamente
 * aplicado e o seletor de loja fica oculto.
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useBroadcastChannel, type BroadcastMessage } from '@/hooks/useBroadcastChannel';
import { useAuth } from '@/features/auth';
import type { DashboardFilters, FilterOptions } from '@/hooks/useDashboardMetrics';
import type { FilterPreset } from '@/components/dashboard/FilterSidebar';

interface FilterContextType {
  // Filtros atuais
  filters: DashboardFilters;
  setFilters: (filters: DashboardFilters) => void;
  clearFilters: () => void;

  // Opções de filtro disponíveis (preenchidas pelo useDashboardMetrics)
  filterOptions: FilterOptions;
  setFilterOptions: (options: FilterOptions) => void;

  // Contagem de filtros ativos
  activeFiltersCount: number;

  // Helpers
  updateFilter: <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => void;

  // Segurança: indica se usuário tem restrição de loja
  hasStoreRestriction: boolean;
  restrictedStore: string | null;

  // Preset de filtros atual (usado pela sidebar para saber quais filtros mostrar)
  currentPreset: FilterPreset;
  setCurrentPreset: (preset: FilterPreset) => void;

  // Controle de visibilidade da sidebar (páginas com filtros próprios podem ocultar)
  isSidebarHidden: boolean;
  setIsSidebarHidden: (hidden: boolean) => void;

  // Estado de expansão do FilterSidebar (compartilhado para outros componentes)
  isFilterSidebarExpanded: boolean;
  setIsFilterSidebarExpanded: (expanded: boolean) => void;

  // Controle de bloqueio de filtro de lojas (usado na Distribuição de Lucros)
  isStoreFilterLocked: boolean;
  setStoreFilterLocked: (locked: boolean) => void;
  lockedStoreFilterMessage: string | null;
  setLockedStoreFilterMessage: (message: string | null) => void;
}

// Chaves do localStorage para sincronizar filtros entre abas
const FILTERS_STORAGE_KEY = 'd12-dashboard-filters';
const FILTER_OPTIONS_STORAGE_KEY = 'd12-dashboard-filter-options';

// Lojas padrão D12
const D12_LOJAS = ['Public Store', 'XBottles'];

const FilterContext = createContext<FilterContextType | undefined>(undefined);

/**
 * Salva os filtros no localStorage
 */
function saveFiltersToStorage(filters: DashboardFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignorar erros de localStorage
  }
}

/**
 * Carrega os filtros do localStorage
 */
function loadFiltersFromStorage(): DashboardFilters | null {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignorar erros de parse
  }
  return null;
}

/**
 * Salva as opções de filtro no localStorage
 */
function saveFilterOptionsToStorage(options: FilterOptions) {
  try {
    localStorage.setItem(FILTER_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  } catch {
    // Ignorar erros de localStorage
  }
}

/**
 * Carrega as opções de filtro do localStorage
 */
function loadFilterOptionsFromStorage(): FilterOptions | null {
  try {
    const stored = localStorage.getItem(FILTER_OPTIONS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignorar erros de parse
  }
  return null;
}

export function FilterProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const isHandlingCrossTabEventRef = useRef(false);

  // SEGURANÇA: Obter restrição de loja do usuário
  const { hasStoreRestriction, restrictedStore } = useAuth();

  // Handler para mensagens de outras abas
  const handleCrossTabMessage = useCallback((message: BroadcastMessage) => {
    if (message.type !== 'FILTERS_CHANGED') return;

    // Evitar loops
    if (isHandlingCrossTabEventRef.current) return;
    isHandlingCrossTabEventRef.current = true;

    try {
      const payload = message.payload as DashboardFilters | undefined;
      if (payload) {
        // Atualizar estado local sem triggar outro broadcast
        setFiltersState(payload);

        // Invalidar cache para forçar refetch com novos parâmetros
        queryClient.invalidateQueries({ queryKey: ['dashboard-vendas'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-metaads-daily'] });
        queryClient.invalidateQueries({ queryKey: ['financial-data'] });
      }
    } finally {
      setTimeout(() => {
        isHandlingCrossTabEventRef.current = false;
      }, 100);
    }
  }, [queryClient]);

  // Inicializar BroadcastChannel
  const { postMessage } = useBroadcastChannel(handleCrossTabMessage);

  // Determinar filtros iniciais
  const getInitialFilters = useCallback((): DashboardFilters => {
    // SEGURANÇA: Se usuário tem restrição de loja, forçar filtro
    if (hasStoreRestriction && restrictedStore) {
      return { loja: [restrictedStore] };
    }

    // Tentar carregar do localStorage
    const storedFilters = loadFiltersFromStorage();
    if (storedFilters) {
      return storedFilters;
    }

    // Padrão: Lojas D12
    return { loja: D12_LOJAS };
  }, [hasStoreRestriction, restrictedStore]);

  // Estado dos filtros
  const [filters, setFiltersState] = useState<DashboardFilters>(getInitialFilters);

  // Estado do preset de filtros (define quais filtros aparecem na sidebar)
  const [currentPreset, setCurrentPreset] = useState<FilterPreset>('all');

  // Estado de visibilidade da sidebar (páginas com filtros próprios podem ocultar)
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);

  // Estado de expansão do FilterSidebar (compartilhado para outros componentes)
  const [isFilterSidebarExpanded, setIsFilterSidebarExpanded] = useState(false);

  // Controle de bloqueio de filtro de lojas (usado na Distribuição de Lucros)
  const [isStoreFilterLocked, setStoreFilterLocked] = useState(false);
  const [lockedStoreFilterMessage, setLockedStoreFilterMessage] = useState<string | null>(null);

  // Estado das opções de filtro (preenchidas pelo useDashboardMetrics e persistidas no localStorage)
  const [filterOptions, setFilterOptionsState] = useState<FilterOptions>(() => {
    // Tentar carregar do localStorage primeiro
    const storedOptions = loadFilterOptionsFromStorage();
    if (storedOptions) {
      return storedOptions;
    }
    // Valores padrão vazios
    return {
      lojas: [],
      produtos: [],
      formasPagamento: [],
      tiposItem: [],
      afiliados: [],
      skus: [],
      statusList: [],
    };
  });

  // Wrapper que persiste as opções no localStorage quando atualizadas
  // SEGURANÇA: Filtrar opções de lojas para usuários com restrição
  const setFilterOptions = useCallback((options: FilterOptions) => {
    // Se usuário tem restrição de loja, mostrar apenas a loja restrita nas opções
    if (hasStoreRestriction && restrictedStore) {
      options = {
        ...options,
        lojas: options.lojas.filter(loja =>
          loja.toLowerCase() === restrictedStore.toLowerCase()
        )
      };
    }
    setFilterOptionsState(options);
    saveFilterOptionsToStorage(options);
  }, [hasStoreRestriction, restrictedStore]);

  // SEGURANÇA: Atualizar filtros quando restrição de loja mudar
  useEffect(() => {
    if (hasStoreRestriction && restrictedStore) {
      // Forçar filtro para loja restrita
      setFiltersState({ loja: [restrictedStore] });
    }
  }, [hasStoreRestriction, restrictedStore]);

  // Função para atualizar filtros com debounce e invalidação de cache
  const setFilters = useCallback((newFilters: DashboardFilters) => {
    // BLOQUEIO: Se filtro de lojas está bloqueado, verificar se tentou alterar
    if (isStoreFilterLocked && newFilters.loja !== undefined) {
      // Verificar se a mudança é realmente diferente do estado atual
      const currentLojas = filters.loja || [];
      const newLojas = newFilters.loja || [];
      const isDifferent =
        currentLojas.length !== newLojas.length ||
        !currentLojas.every(loja => newLojas.includes(loja));

      if (isDifferent) {
        toast.error(lockedStoreFilterMessage || 'Filtro de lojas bloqueado nesta página', {
          duration: 4000,
        });
        return; // Não aplicar a mudança
      }
    }

    // SEGURANÇA: Se usuário tem restrição, sempre forçar loja restrita
    if (hasStoreRestriction && restrictedStore) {
      newFilters = { ...newFilters, loja: [restrictedStore] };
    }

    // Se está processando evento de outra aba, não fazer broadcast
    if (isHandlingCrossTabEventRef.current) {
      setFiltersState(newFilters);
      return;
    }

    // Limpar timer anterior se existir
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Aplicar debounce de 300ms
    debounceTimerRef.current = setTimeout(() => {
      // Cancelar queries em andamento
      queryClient.cancelQueries({ queryKey: ['dashboard-vendas'] });
      queryClient.cancelQueries({ queryKey: ['dashboard-metaads-daily'] });
      queryClient.cancelQueries({ queryKey: ['financial-data'] });

      // Atualizar state
      setFiltersState(newFilters);

      // Salvar no localStorage para sincronização
      saveFiltersToStorage(newFilters);

      // Notificar outras abas
      postMessage('FILTERS_CHANGED', newFilters);

      // Invalidar cache
      queryClient.invalidateQueries({ queryKey: ['dashboard-vendas'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metaads-daily'] });
      queryClient.invalidateQueries({ queryKey: ['financial-data'] });
    }, 300);
  }, [queryClient, postMessage, hasStoreRestriction, restrictedStore, isStoreFilterLocked, lockedStoreFilterMessage, filters]);

  // Função para limpar todos os filtros
  const clearFilters = useCallback(() => {
    // SEGURANÇA: Manter loja restrita se usuário tiver restrição
    if (hasStoreRestriction && restrictedStore) {
      setFilters({ loja: [restrictedStore] });
    } else {
      setFilters({ loja: D12_LOJAS });
    }
  }, [setFilters, hasStoreRestriction, restrictedStore]);

  // Helper para atualizar um filtro específico
  const updateFilter = useCallback(<K extends keyof DashboardFilters>(
    key: K,
    value: DashboardFilters[K]
  ) => {
    const newFilters = { ...filters };
    if (Array.isArray(value) && value.length === 0) {
      delete newFilters[key];
    } else if (value === null || value === undefined) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    setFilters(newFilters);
  }, [filters, setFilters]);

  // SEGURANÇA: Filtrar as opções de lojas expostas para usuários com restrição
  // Isso garante que mesmo se as opções foram carregadas do localStorage com todas as lojas,
  // o usuário restrito só verá a loja dele
  const filteredFilterOptions = useMemo((): FilterOptions => {
    if (hasStoreRestriction && restrictedStore) {
      return {
        ...filterOptions,
        lojas: filterOptions.lojas.filter(loja =>
          loja.toLowerCase() === restrictedStore.toLowerCase()
        )
      };
    }
    return filterOptions;
  }, [filterOptions, hasStoreRestriction, restrictedStore]);

  // Contar filtros ativos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.loja && filters.loja.length > 0) count++;
    if (filters.produto_nome && filters.produto_nome.length > 0) count++;
    if (filters.forma_pagamento && filters.forma_pagamento.length > 0) count++;
    if (filters.tipo_item_venda_texto && filters.tipo_item_venda_texto.length > 0) count++;
    if (filters.afiliado_nome && filters.afiliado_nome.length > 0) count++;
    if (filters.sku && filters.sku.length > 0) count++;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.has_affiliate !== null && filters.has_affiliate !== undefined) count++;
    return count;
  }, [filters]);

  // Escutar mudanças no localStorage (fallback para navegadores sem BroadcastChannel)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== FILTERS_STORAGE_KEY || !event.newValue) return;

      // Evitar loops
      if (isHandlingCrossTabEventRef.current) return;
      isHandlingCrossTabEventRef.current = true;

      try {
        const newFilters = JSON.parse(event.newValue) as DashboardFilters;

        // SEGURANÇA: Se usuário tem restrição, ignorar mudanças de loja
        if (hasStoreRestriction && restrictedStore) {
          newFilters.loja = [restrictedStore];
        }

        setFiltersState(newFilters);

        // Invalidar cache
        queryClient.invalidateQueries({ queryKey: ['dashboard-vendas'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-metaads-daily'] });
        queryClient.invalidateQueries({ queryKey: ['financial-data'] });
      } catch {
        // Ignorar erros de parse
      } finally {
        setTimeout(() => {
          isHandlingCrossTabEventRef.current = false;
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [queryClient, hasStoreRestriction, restrictedStore]);

  return (
    <FilterContext.Provider
      value={{
        filters,
        setFilters,
        clearFilters,
        filterOptions: filteredFilterOptions, // SEGURANÇA: Usar opções filtradas
        setFilterOptions,
        activeFiltersCount,
        updateFilter,
        hasStoreRestriction,
        restrictedStore,
        currentPreset,
        setCurrentPreset,
        isSidebarHidden,
        setIsSidebarHidden,
        isFilterSidebarExpanded,
        setIsFilterSidebarExpanded,
        isStoreFilterLocked,
        setStoreFilterLocked,
        lockedStoreFilterMessage,
        setLockedStoreFilterMessage,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
